import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl, IntlShape } from 'react-intl';
import classnames from 'classnames';
import { extractInstructions } from '@utils/miscellaneous/extractInstructions';

// Constants

import { INSTRUCTIONAL_TEXT_STYLE_TYPES } from '@constants/common/styleTypes';

// Types

import { InstructionalTextPropTypes } from '@components/ssap/SsapTypes';
import { useIntlTagFormatter } from '@utils/miscellaneous/react-intl-utlis';


const InstructionalText = (props: InstructionalTextPropTypes): ReactElement => {

    const {
        MODAL,
        PARAGRAPH
    } = INSTRUCTIONAL_TEXT_STYLE_TYPES;

    const {
        anchor,
        Component,
        disabled,
        expandClassName = '',
        styleType = PARAGRAPH,
        required,
        replacements = {},
        childrenReplacements = {},
        reactPortalAnchor: {
            current: portalContainer
        } = {},
        skipParsing = false,
        onlyInstructions = false,
        fadeEffectDisabled = false
    } = props;

    // If 'data-instructions' attribute for HTML tags or dataInstructions prop for React components is skipped
    // the link and corresponding instructions will be omitted and Component will be rendered as-is

    // If 'onlyInstructions' is passed as true it forces to render only instructions,
    // there are no Learn More link, no cross button, instructions persist constantly

    const INSTRUCTIONS_PROP_NAME: string = 'data-instructions';

    const {
        id,
        dataTestId
    } = extractInstructions(Component.props[INSTRUCTIONS_PROP_NAME]);

    const intlApi: IntlShape = useIntl();

    type VisibilityStateType = boolean;

    const [isShown, toggleView] = useState<VisibilityStateType>(onlyInstructions);

    useEffect((): void => {
        toggleView(onlyInstructions);
    }, [onlyInstructions]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void | null => {
        e.preventDefault();
        e.stopPropagation();

        toggleView((isShown: boolean) => !isShown);
    };

    const intlTagFormatter = useIntlTagFormatter(skipParsing);
    const text = intlTagFormatter(id ? id : 'common.no_text', replacements);

    const modalClassName: string = classnames(
        'instruction_link',
        'tablet:grid-col-4 gi-modal__txt-right'
    );

    const paragraphClassName: string = classnames(
        'instruction_link',
        'instruction_link_p'
    );

    const linkClassName: string = classnames(
        {
            [modalClassName]: styleType === MODAL,
            [paragraphClassName]: styleType === PARAGRAPH
        }
    );

    const link: ReactElement | null = id && !disabled
        ? React.cloneElement(
            <a
                href = '#'
                onClick = { handleClick }
                className = { linkClassName }
                id = { id }
                key = { id }
                data-testid = { `${dataTestId}_link` }
            >
                { intlApi.formatMessage({ id: anchor ? anchor : 'instructional_text.learn_more_anchor' }) }
            </a>
        )
        : null;

    const WrappedComponent: React.ReactElement<any, any> = React.cloneElement(Component, {

        ...(React.isValidElement(Component.props.children) ?
            {
                children: React.cloneElement(Component.props.children, {
                    children:
                        [
                            ...(Component.props.children.props.children ? Component.props.children.props.children : []),
                            ...(onlyInstructions ? [] : [ link ])
                        ]
                })
            } :
            {
                children:
                    React.Children.toArray([
                        Component.props.children
                            ? intlApi.formatMessage({ id: Component.props.children }, childrenReplacements)
                            : Component.props.children,
                        ...(required ? [ <span key='required' className = 'required-true' /> ] : []),
                        ...(onlyInstructions ? [] : [ link ])
                    ])
            })
    });

    const fadeEffectEnabledClassName: string = classnames(
        {
            'text-expand': !fadeEffectDisabled,
            [expandClassName]: !!expandClassName
        }
    );

    const wrapperClassName: string = classnames(
        {
            [fadeEffectEnabledClassName]: isShown,
            'text-collapse': !isShown
        }
    );

    const alertClassName: string = classnames(
        {
            'usa-alert gi-alert gi-alert__left-accent gi-alert__info expandable': isShown,
            'hidden': !isShown
        }
    );

    const instructions: ReactElement | null = id
        ? (
            <div className = { wrapperClassName } aria-live='polite' aria-atomic='true'>
                <div className = { alertClassName }>
                    <div className="gi-alert__body">
                        <p className="gi-alert__text" data-testid ={`${dataTestId}_notice`}>{text}</p>
                        {
                            !onlyInstructions &&
                            <button
                                className = 'expandable__btn--close usa-button--unstyled gi-alert__icon'
                                onClick = { handleClick }
                                aria-label="Close"
                                data-testid = { `${dataTestId}_close_btn` }
                            >
                                <FontAwesomeIcon icon = 'times' />
                            </button>
                        }
                    </div>
                </div>
            </div>
        )
        : null;

    return (
        <Fragment >
            { WrappedComponent }
            { portalContainer ? ReactDOM.createPortal(instructions, portalContainer) : instructions }
        </Fragment>
    );
};

export default InstructionalText;